import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Lock, Mail, Eye, EyeOff, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    // Determine where to redirect after successful login
    const from = (location.state as any)?.from?.pathname || '/admin';

    // If the user is already logged in, redirect them immediately to /admin
    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                navigate('/admin', { replace: true });
            }
        });
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            setErrorMsg('Por favor rellena todos los campos');
            return;
        }

        setIsLoading(true);
        setErrorMsg(null);

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password: password.trim()
            });

            if (error) {
                // Friendly error translation
                if (error.message.includes('Invalid login credentials')) {
                    throw new Error('Credenciales inválidas. Verifica tu correo y contraseña.');
                }
                throw error;
            }

            if (data?.session) {
                navigate(from, { replace: true });
            }
        } catch (err: any) {
            console.error('Error logging in:', err);
            setErrorMsg(err.message || 'Ocurrió un error inesperado al iniciar sesión.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div 
            className="min-h-screen text-white flex items-center justify-center p-6 relative font-sans bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/login_cinematic_bg.png')" }}
        >
            {/* Dark overlay with slight blur for high contrast */}
            <div className="absolute inset-0 bg-black/65 backdrop-blur-[1px] pointer-events-none" />

            <div className="w-full max-w-md relative z-10 space-y-6">
                {/* Back Link */}
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-[#00d4ff] transition-colors drop-shadow-md"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Volver al sitio web</span>
                </Link>

                {/* Login Card Container */}
                <div className="bg-black/40 border border-white/10 p-8 lg:p-10 rounded-[20px] shadow-[0_50px_100px_rgba(0,0,0,0.95)] backdrop-blur-xl space-y-8">
                    {/* Header */}
                    <div className="text-center space-y-4">
                        <div className="flex flex-col items-center justify-center">
                            <img 
                                src="/logo-fisura-white.png" 
                                className="h-16 w-auto object-contain" 
                                alt="Fisura Expeditions" 
                            />
                            <span className="text-[10px] text-[#00d4ff] font-black tracking-[0.42em] uppercase mt-2.5 ml-1 select-none">
                                Backoffice
                            </span>
                        </div>
                        <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 pt-2">
                            Área segura de administración
                        </p>
                    </div>

                    {/* Error Box */}
                    {errorMsg && (
                        <div className="bg-red-500/10 border-2 border-red-500/30 p-4 rounded-[10px] flex gap-3 items-start animate-in fade-in slide-in-from-top-2 duration-300">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            <p className="text-red-400 text-xs font-bold uppercase tracking-wider leading-relaxed">
                                {errorMsg}
                            </p>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-4">
                            {/* Email Input */}
                            <div className="space-y-2">
                                <label className="text-[9px] font-bold uppercase tracking-[0.2em] text-gray-500 block">Correo Electrónico</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                                    <input 
                                        type="email" 
                                        required
                                        className="w-full bg-black/60 border border-[#2d2621] focus:border-[#00d4ff] text-sm text-white pl-12 pr-4 py-3.5 outline-none transition-all rounded-[10px]"
                                        placeholder="correo@fisuraexpeditions.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Password Input */}
                            <div className="space-y-2">
                                <label className="text-[9px] font-bold uppercase tracking-[0.2em] text-gray-500 block">Contraseña</label>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        required
                                        className="w-full bg-black/60 border border-[#2d2621] focus:border-[#00d4ff] text-sm text-white pl-12 pr-12 py-3.5 outline-none transition-all rounded-[10px]"
                                        placeholder="••••••••••••"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        disabled={isLoading}
                                    />
                                    <button 
                                        type="button"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-white transition-colors"
                                        title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                        disabled={isLoading}
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button 
                            type="submit"
                            disabled={isLoading}
                            className={`w-full bg-[#00d4ff] text-black py-4 font-title text-xs uppercase tracking-[0.2em] hover:bg-white transition-all rounded-[10px] shadow-xl flex items-center justify-center gap-2 ${
                                isLoading ? 'opacity-70 cursor-not-allowed' : ''
                            }`}
                        >
                            {isLoading ? <Loader2 className="animate-spin w-4 h-4" /> : null}
                            <span>{isLoading ? 'Iniciando Sesión...' : 'Iniciar Sesión'}</span>
                        </button>
                    </form>
                </div>

                {/* Footer Signature */}
                <p className="text-center text-[9px] font-bold uppercase tracking-widest text-gray-700">
                    Fisura Expeditions &copy; 2026 • Secure Admin Area
                </p>
            </div>
        </div>
    );
};

export default Login;
